/* @flow */
import * as React from 'react';

import classnames from 'classnames';
import './badge.module.css';

export type BadgeColor = 'grey' | 'blue' | 'orange' | 'rose';

export type Props = {
    contents?: React.Node,
    hideBadgeWhenEmpty?: boolean,
    color?: BadgeColor,
    onClick?: (SyntheticEvent<>) => void,
    shouldFlash?: boolean,
};

/**
 * This is intended to be a generic UI element that can be added to another
 * component to indicate the presence of notifications (although we don't use it
 * for that purpose so far), or the presense of applied filters (what we _do_
 * use it for currently).
 *
 * It should be added within a container with a `position: relative` style,
 * because it uses absolute positioning to appear in the top-right corner on top
 * of the container.
 */

export function Badge(props: Props) {
    const {contents, hideBadgeWhenEmpty, color = 'grey', onClick, shouldFlash} = props;
    const [firstLoad, setFirstLoad] = React.useState(true);
    const prevContentsRef = React.useRef(contents);

    React.useEffect(() => {
        if (prevContentsRef.current !== contents) {
            setFirstLoad(false);
            prevContentsRef.current = contents;
        }
    }, [contents]);

    const containerStyleName = classnames(
        {
            container: contents,
            'container--small': !contents && !hideBadgeWhenEmpty && !firstLoad,
            'container--small-initial': !contents && !hideBadgeWhenEmpty && firstLoad,
            'container--hidden': !contents && hideBadgeWhenEmpty,
            clickable: onClick,
            beacon: shouldFlash,
        },
        color
    );

    return (
        <div styleName={containerStyleName} onClick={onClick}>
            <span styleName='contents'>{contents}</span>
        </div>
    );
}
